import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { clearSsoSession } from '@/services/api';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('@/views/Callback.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: { requiresAuth: true },
  },
  // User Management
  {
    path: '/users',
    name: 'Users',
    component: () => import('@/views/users/UserList.vue'),
    meta: { requiresAuth: true, permission: 'users:read' },
  },
  {
    path: '/users/new',
    name: 'CreateUser',
    component: () => import('@/views/users/UserForm.vue'),
    meta: { requiresAuth: true, permission: 'users:create' },
  },
  {
    path: '/users/bulk-invite',
    name: 'BulkInvite',
    component: () => import('@/views/users/BulkInvite.vue'),
    meta: { requiresAuth: true, permission: 'users:create' },
  },
  {
    path: '/users/:id',
    name: 'UserDetail',
    component: () => import('@/views/users/UserDetail.vue'),
    meta: { requiresAuth: true, permission: 'users:read' },
  },
  {
    path: '/users/:id/edit',
    name: 'EditUser',
    component: () => import('@/views/users/UserForm.vue'),
    meta: { requiresAuth: true, permission: 'users:update' },
  },
  // Role Management
  {
    path: '/roles',
    name: 'Roles',
    component: () => import('@/views/roles/RoleList.vue'),
    meta: { requiresAuth: true, permission: 'roles:read' },
  },
  {
    path: '/roles/new',
    name: 'CreateRole',
    component: () => import('@/views/roles/RoleForm.vue'),
    meta: { requiresAuth: true, permission: 'roles:create' },
  },
  {
    path: '/roles/:id',
    name: 'RoleDetail',
    component: () => import('@/views/roles/RoleDetail.vue'),
    meta: { requiresAuth: true, permission: 'roles:read' },
  },
  {
    path: '/roles/:id/edit',
    name: 'EditRole',
    component: () => import('@/views/roles/RoleForm.vue'),
    meta: { requiresAuth: true, permission: 'roles:update' },
  },
  // Group Management
  {
    path: '/groups',
    name: 'Groups',
    component: () => import('@/views/groups/GroupList.vue'),
    meta: { requiresAuth: true, permission: 'groups:read' },
  },
  {
    path: '/groups/new',
    name: 'CreateGroup',
    component: () => import('@/views/groups/GroupForm.vue'),
    meta: { requiresAuth: true, permission: 'groups:create' },
  },
  {
    path: '/groups/:pk',
    name: 'GroupDetail',
    component: () => import('@/views/groups/GroupDetail.vue'),
    meta: { requiresAuth: true, permission: 'groups:read' },
  },
  {
    path: '/groups/:pk/edit',
    name: 'EditGroup',
    component: () => import('@/views/groups/GroupForm.vue'),
    meta: { requiresAuth: true, permission: 'groups:update' },
  },
  // App Management
  {
    path: '/apps',
    name: 'Apps',
    component: () => import('@/views/apps/AppList.vue'),
    meta: { requiresAuth: true, permission: 'apps:read' },
  },
  {
    path: '/apps/new',
    name: 'CreateApp',
    component: () => import('@/views/apps/AppForm.vue'),
    meta: { requiresAuth: true, permission: 'apps:create' },
  },
  {
    path: '/apps/:id',
    name: 'AppDetail',
    component: () => import('@/views/apps/AppDetail.vue'),
    meta: { requiresAuth: true, permission: 'apps:read' },
  },
  {
    path: '/apps/:id/edit',
    name: 'EditApp',
    component: () => import('@/views/apps/AppForm.vue'),
    meta: { requiresAuth: true, permission: 'apps:update' },
  },
  // Organization Management
  {
    path: '/organizations',
    name: 'Organizations',
    component: () => import('@/views/organizations/OrganizationList.vue'),
    meta: { requiresAuth: true, permission: 'organizations:read' },
  },
  {
    path: '/organizations/new',
    name: 'CreateOrganization',
    component: () => import('@/views/organizations/OrganizationForm.vue'),
    meta: { requiresAuth: true, permission: 'organizations:create' },
  },
  {
    path: '/organizations/:id',
    name: 'OrganizationDetail',
    component: () => import('@/views/organizations/OrganizationDetail.vue'),
    meta: { requiresAuth: true, permission: 'organizations:read' },
  },
  {
    path: '/organizations/:id/edit',
    name: 'EditOrganization',
    component: () => import('@/views/organizations/OrganizationForm.vue'),
    meta: { requiresAuth: true, permission: 'organizations:update' },
  },
  // Audit Logs
  {
    path: '/audit-logs',
    name: 'AuditLogs',
    component: () => import('@/views/audit/AuditLogList.vue'),
    meta: { requiresAuth: true, permission: 'audit:read' },
  },
  {
    path: '/audit-logs/:id',
    name: 'AuditLogDetail',
    component: () => import('@/views/audit/AuditLogDetail.vue'),
    meta: { requiresAuth: true, permission: 'audit:read' },
  },
  // API Keys
  {
    path: '/api-keys',
    name: 'ApiKeys',
    component: () => import('@/views/api-keys/ApiKeyList.vue'),
    meta: { requiresAuth: true, permission: 'api-keys:read' },
  },
  // Permissions
  {
    path: '/permissions',
    name: 'Permissions',
    component: () => import('@/views/permissions/PermissionList.vue'),
    meta: { requiresAuth: true, permission: 'permissions:read' },
  },
  // Security
  {
    path: '/security',
    name: 'Security',
    component: () => import('../views/security/SecurityPage.vue'),
    meta: { requiresAuth: true, permission: 'security:manage' },
  },
  // Catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 < Date.now() : false;
  } catch {
    return true;
  }
}

// Restore auth from localStorage (JWT decode, no API call)
function restoreAuthFromStorage(authStore: ReturnType<typeof useAuthStore>): boolean {
  const token = localStorage.getItem('moya_token');
  const userJson = localStorage.getItem('moya_user');
  if (!token || !userJson) return false;

  if (isTokenExpired(token)) {
    // Clear the cookie as well, or the index.html bootstrap re-injects this
    // same expired token on the next load and we redirect to SSO forever.
    clearSsoSession();
    return false;
  }

  try {
    const userData = JSON.parse(userJson);

    authStore.user = {
      id: userData.id || '',
      email: userData.email || '',
      display_name: userData.name || userData.display_name || userData.email || '',
      is_active: true,
      is_superuser: userData.is_superuser || userData.roles?.includes('admin') || userData.roles?.includes('superuser') || false,
      email_verified: true,
      created_at: userData.created_at || new Date().toISOString(),
      permissions: userData.permissions || [],
      roles: (userData.roles || []).map((r: string) => ({
        id: r,
        name: r,
        code: r,
        permissions: userData.permissions || [],
        is_system: false,
        created_at: new Date().toISOString(),
      })),
    } as any;
    return true;
  } catch {
    return false;
  }
}

function redirectToSSO(): void {
  sessionStorage.setItem('moya_auth_return_url', window.location.href);
  const ssoUrl = import.meta.env.VITE_SSO_URL || 'https://login.moyasolutions.com';
  const callbackUrl = `${window.location.origin}/callback`;
  window.location.href = `${ssoUrl}/login?redirect_uri=${encodeURIComponent(callbackUrl)}`;
}

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore();
  const requiresAuth = to.meta.requiresAuth !== false;
  const requiredPermission = to.meta.permission as string | undefined;

  if (requiresAuth && !authStore.isAuthenticated) {
    // Try to restore session from localStorage (SSO tokens + user data)
    if (restoreAuthFromStorage(authStore)) {
      // Restored from localStorage - try loadUser in background (non-blocking)
      authStore.loadUser().catch(() => {
        // API may not support SSO tokens - keep localStorage-based session
      });
    } else {
      // No token — redirect to SSO portal
      redirectToSSO();
      return;
    }
  }

  if (requiredPermission && !authStore.hasPermission(requiredPermission)) {
    next({ name: 'Dashboard' });
    return;
  }

  next();
});

export default router;
